import { encode } from '../../../lib/base64';
import { AllWorldRoutes } from '../../../routes/parts/allWorldRoutes';
import { AnomalyType } from './compositions/anomalyTypePicker';

export interface AnomalyExaminationPathParams {
  deviceId: string,
  /** Omit to let the examination page default to the latest day flagged as an anomaly for this
   * device/type - used by the top-level anomalies table's links (see useDeviceAnomaliesListColumns.tsx). */
  date?: number,
  type: AnomalyType
}

/**
 * Builds the link to the shared device anomaly examination page for a given examination page path,
 * anomaly type and (optionally) examined day. Takes the path as a plain string - rather than the
 * whole routes object - so a caller that already holds just routes.core.deviceAnomalyExamination
 * (e.g. a useMemo callback that depends on that one field, see useDeviceAnomaliesListColumns.tsx)
 * can call this directly instead of getAnomalyExaminationPath, avoiding react-hooks/exhaustive-deps
 * treating the whole routes object as a dependency.
 */
export function buildAnomalyExaminationPath(examinationPath: string, params: AnomalyExaminationPathParams): string {
  const { deviceId, date, type } = params;
  const searchParams = new URLSearchParams();
  searchParams.set('id', encode(deviceId));
  if (date !== undefined) {
    searchParams.set('date', String(date));
  }
  searchParams.set('type', type);
  return `${examinationPath}?${searchParams.toString()}`;
}

/**
 * Builds the link to the shared device anomaly examination page for a given device, anomaly type
 * and (optionally) examined day. Shared by every anomaly chart that offers a click-through, plus
 * the top-level anomalies table, so all of them agree on the query param names that
 * core-device-anomaly-examination-route.tsx parses back out. Omitting `date` lets the
 * examination page itself default to the latest day flagged as an anomaly for that device/type.
 */
export function getAnomalyExaminationPath(routes: AllWorldRoutes, params: AnomalyExaminationPathParams): string {
  return buildAnomalyExaminationPath(routes.core.deviceAnomalyExamination, params);
}
